import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import type { ColorFormat } from '@sorare/core/src/lib/colors';
import { getRgbColorFromHex } from '@sorare/core/src/lib/colors';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import streakBackground from 'assets/streakBackground.webp';
import { StreakProgressIndicator } from 'components/missions/Streaks/StreakProgressIndicator';

import type { FixtureOverviewTasks_so5Fixture } from './__generated__/index.graphql';

const TaskRoot = styled(LightedBezel)`
  background: var(--custom-background);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--triple-unit);
`;

const Logo = styled.img`
  height: 36px;
  max-width: 120px;
  object-fit: contain;
`;

const Icon = styled.img`
  position: absolute;
  object-fit: contain;
  opacity: 0.1;
`;

type Props = {
  so5Fixture?: FixtureOverviewTasks_so5Fixture;
};

const MINIMUM_ITEMS_COUNT = 2;

const Item = ({
  so5LeaderboardGroup,
}: {
  so5LeaderboardGroup: FixtureOverviewTasks_so5Fixture['so5LeaderboardGroups'][number];
}) => {
  const hasReachedOneStreak = so5LeaderboardGroup.myThresholdsStreakTasks.some(
    task =>
      (task.currentThresholdStatus.liveScore || 0) >=
      task.currentThreshold.score
  );
  const mainColor = hasReachedOneStreak
    ? '#FEF314'
    : so5LeaderboardGroup.primaryColor;
  return (
    <TaskRoot
      key={so5LeaderboardGroup.slug}
      bezelSize={16}
      color={hasReachedOneStreak ? '#FF4E22' : 'transparent'}
      borderColor={
        hasReachedOneStreak
          ? 'linear-gradient(170deg, #FF4E22 20%, #FEF314 100%)'
          : `linear-gradient(to bottom, var(--c-nd-100) 80%, ${mainColor} 100%)`
      }
      style={
        {
          '--c-rgb-color': getRgbColorFromHex(mainColor as ColorFormat),
          '--custom-background': hasReachedOneStreak
            ? `radial-gradient(46% 60% at 50% 100%,rgba(254, 243, 20, 0.3) 5%, transparent 100%), conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px, url(${streakBackground}) center/cover no-repeat`
            : `radial-gradient(46% 60% at 50% 100%,rgba(var(--c-rgb-color), 0.3) 5%, transparent 100%), conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px, rgba(0, 0, 0, 0.5)`,
        } as CSSProperties
      }
    >
      <Logo
        height={36}
        src={so5LeaderboardGroup.logoUrl}
        alt={so5LeaderboardGroup.slug}
      />
      <Icon
        height={158}
        src={so5LeaderboardGroup.iconUrl}
        alt={so5LeaderboardGroup.slug}
      />
      <Horizontal gap={1.5}>
        {so5LeaderboardGroup.myThresholdsStreakTasks.map(task => {
          return (
            <StreakProgressIndicator key={task.id} task={task} size="sm" />
          );
        })}
      </Horizontal>
    </TaskRoot>
  );
};

export const FixtureOverviewTasks = ({ so5Fixture }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const { so5LeaderboardGroups } = so5Fixture || {};

  const leaderboardGroupsWithHotStreak = so5LeaderboardGroups?.filter(
    group => group.myThresholdsStreakTasks?.length > 0
  );

  if (!leaderboardGroupsWithHotStreak?.length) return null;

  const first2leaderboardGroupsWithHotStreak =
    leaderboardGroupsWithHotStreak.slice(0, MINIMUM_ITEMS_COUNT);
  const otherLeaderboardGroupsWithHotStreak =
    leaderboardGroupsWithHotStreak.slice(MINIMUM_ITEMS_COUNT);

  return (
    <Vertical gap={1.5}>
      <Horizontal>
        <Flame />
        <LabelM bold color="var(--c-nd-600)">
          <FormattedMessage {...fantasy.hotStreak} />
        </LabelM>
      </Horizontal>
      <Vertical>
        {first2leaderboardGroupsWithHotStreak.map(group => (
          <Item key={group.slug} so5LeaderboardGroup={group} />
        ))}
        <Collapse open={expanded} gap={0}>
          <Vertical>
            {otherLeaderboardGroupsWithHotStreak.map(group => (
              <Item key={group.slug} so5LeaderboardGroup={group} />
            ))}
          </Vertical>
        </Collapse>
        {leaderboardGroupsWithHotStreak.length > MINIMUM_ITEMS_COUNT &&
          !expanded && (
            <Button
              color="quaternary"
              size="small"
              onClick={() => setExpanded(true)}
            >
              <FormattedMessage {...glossary.showMore} />
            </Button>
          )}
        {!!expanded && (
          <Button
            color="quaternary"
            size="small"
            onClick={() => setExpanded(false)}
          >
            <FormattedMessage {...glossary.showLess} />
          </Button>
        )}
      </Vertical>
    </Vertical>
  );
};

FixtureOverviewTasks.fragments = {
  so5Fixture: gql`
    fragment FixtureOverviewTasks_so5Fixture on So5Fixture {
      slug
      so5LeaderboardGroups(groupType: COMPETITION) {
        slug
        logoUrl
        iconUrl
        primaryColor: color(variant: PRIMARY)
        myThresholdsStreakTasks {
          id
          currentThresholdStatus {
            liveScore
          }
          currentThreshold {
            score
          }
          ...StreakProgressIndicator_thresholdsStreakTask
        }
      }
    }
    ${StreakProgressIndicator.fragments.thresholdsStreakTask}
  ` as TypedDocumentNode<FixtureOverviewTasks_so5Fixture>,
};
